import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Search, UserRound, X, Users2, UserCog, FileText, Archive } from "lucide-react";
import { RelationsManager } from "@/components/office/RelationsManager";
import { RelationsArchive } from "@/components/office/RelationsArchive";

// Autocomplete operatore: cerca lavoratori compatibili con l'appalto selezionato.
function EducatorSearch({ appaltoId, service, onPick, placeholder = "Cerca operatore…", testid }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState([]);
  useEffect(() => {
    if (!appaltoId) return;
    const t = setTimeout(async () => {
      try {
        const r = await api.get(`/office/relations/workers?appalto_id=${appaltoId}&service=${service}&q=${encodeURIComponent(q)}`);
        setResults(r.data || []);
      } catch (_) { setResults([]); }
    }, 200);
    return () => clearTimeout(t);
  }, [q, appaltoId, service]);
  return (
    <div className="relative">
      <div className="flex items-center gap-2 rounded-lg border border-slate-200 px-2.5 py-1.5 bg-white">
        <Search className="h-4 w-4 text-slate-400" />
        <input
          className="w-full text-sm outline-none bg-transparent"
          placeholder={placeholder}
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          data-testid={testid}
        />
      </div>
      {open && results.length > 0 && (
        <div className="absolute z-20 mt-1 w-full rounded-lg border border-slate-200 bg-white shadow-lg max-h-56 overflow-auto" data-testid={`${testid}-results`}>
          {results.map((r) => (
            <button
              key={r.id}
              className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center gap-2"
              onClick={() => { onPick(r); setOpen(false); setQ(""); }}
              data-testid={`${testid}-opt-${r.id}`}
            >
              <UserRound className="h-3.5 w-3.5 text-slate-400" /> {r.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function EducatoriTitolari() {
  const [appalti, setAppalti] = useState([]);
  const [appaltoId, setAppaltoId] = useState("");
  const [services, setServices] = useState([]);
  const [service, setService] = useState("");
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState("all"); // all | assigned | unassigned
  const [selected, setSelected] = useState(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/office/relations/appalti").then((r) => setAppalti(r.data || [])).catch(() => setAppalti([])).finally(() => setLoading(false));
  }, []);
  useEffect(() => {
    setService(""); setServices([]); setUsers([]); setSelected(new Set());
    if (!appaltoId) return;
    api.get(`/office/relations/services?appalto_id=${appaltoId}`).then((r) => setServices(r.data || [])).catch(() => setServices([]));
  }, [appaltoId]);

  const loadUsers = async () => {
    if (!appaltoId || !service) return;
    try {
      const r = await api.get(`/office/relations/users?appalto_id=${appaltoId}&service=${service}`);
      setUsers(r.data || []); setSelected(new Set());
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { loadUsers(); /* eslint-disable-next-line */ }, [service]);

  const shown = useMemo(() => users.filter((u) => filter === "all" ? true : filter === "assigned" ? (u.titular_worker_ids || []).length > 0 : (u.titular_worker_ids || []).length === 0), [users, filter]);

  const setTitolari = async (userIds, workerIds) => {
    try {
      await api.post("/office/relations/assign", { appalto_id: appaltoId, service, user_ids: userIds, worker_ids: workerIds });
      await loadUsers();
      return true;
    } catch (e) { toast.error(formatApiError(e)); return false; }
  };
  const addTitolare = async (u, ed) => {
    const cur = u.titular_worker_ids || [];
    if (cur.includes(ed.id)) return;
    if (await setTitolari([u.id], [...cur, ed.id])) toast.success("Operatore titolare aggiunto");
  };
  const removeTitolare = async (u, wid) => {
    if (await setTitolari([u.id], (u.titular_worker_ids || []).filter((x) => x !== wid))) toast.success("Operatore rimosso");
  };
  const bulkAdd = async (ed) => {
    for (const uid of selected) {
      const u = users.find((x) => x.id === uid);
      const cur = u?.titular_worker_ids || [];
      if (!cur.includes(ed.id)) {
        await api.post("/office/relations/assign", { appalto_id: appaltoId, service, user_ids: [uid], worker_ids: [...cur, ed.id] }).catch(() => {});
      }
    }
    toast.success(`Operatore aggiunto a ${selected.size} utenti`);
    setBulkOpen(false); await loadUsers();
  };

  const toggleSel = (id) => setSelected((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allShownSelected = shown.length > 0 && shown.every((u) => selected.has(u.id));

  if (loading) return <p className="text-sm text-slate-500 p-4">Caricamento…</p>;

  if (appalti.length === 0) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-8 text-center" data-testid="relations-empty">
        <Users2 className="h-8 w-8 text-slate-300 mx-auto mb-3" />
        <p className="text-sm text-slate-500">Non sono presenti servizi con utenti disponibili per la gestione delle relazioni.</p>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="relations-admin">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-medium text-slate-500 mb-1 block">Appalto</label>
          <Select value={appaltoId} onValueChange={setAppaltoId}>
            <SelectTrigger data-testid="relations-appalto"><SelectValue placeholder="Seleziona appalto" /></SelectTrigger>
            <SelectContent>{appalti.map((a) => <SelectItem key={a.id} value={a.id}>{a.nome}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs font-medium text-slate-500 mb-1 block">Servizio</label>
          <Select value={service} onValueChange={setService} disabled={!appaltoId}>
            <SelectTrigger data-testid="relations-service"><SelectValue placeholder="Seleziona servizio" /></SelectTrigger>
            <SelectContent>{services.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      {appaltoId && service && (
        <>
          <div className="flex flex-wrap items-center gap-2 justify-between">
            <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5" data-testid="relations-filters">
              {[["all", "Tutti"], ["assigned", "Assegnati"], ["unassigned", "Senza operatore"]].map(([k, l]) => (
                <button key={k} onClick={() => setFilter(k)}
                  className={`px-3 py-1.5 text-sm rounded-md transition-colors ${filter === k ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"}`}
                  data-testid={`relations-filter-${k}`}>{l}</button>
              ))}
            </div>
            {selected.size > 0 && (
              <Button size="sm" onClick={() => setBulkOpen((v) => !v)} data-testid="relations-bulk-btn">
                Aggiungi operatore ({selected.size})
              </Button>
            )}
          </div>

          {bulkOpen && selected.size > 0 && (
            <div className="rounded-lg border border-indigo-200 bg-indigo-50/60 p-3" data-testid="relations-bulk-panel">
              <p className="text-xs text-slate-600 mb-2">Aggiungi un operatore titolare a {selected.size} utenti selezionati</p>
              <EducatorSearch appaltoId={appaltoId} service={service} testid="relations-bulk-search"
                onPick={bulkAdd} />
            </div>
          )}

          <div className="rounded-xl border border-slate-200 overflow-hidden bg-white">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500">
                <tr>
                  <th className="w-10 p-2">
                    <Checkbox checked={allShownSelected}
                      onCheckedChange={() => setSelected((prev) => {
                        const n = new Set(prev); const all = shown.every((u) => n.has(u.id));
                        shown.forEach((u) => all ? n.delete(u.id) : n.add(u.id)); return n;
                      })} data-testid="relations-select-all" />
                  </th>
                  <th className="text-left font-medium p-2">Utente</th>
                  <th className="text-left font-medium p-2">Operatori titolari</th>
                </tr>
              </thead>
              <tbody>
                {shown.length === 0 && (
                  <tr><td colSpan={3} className="p-6 text-center text-slate-400" data-testid="relations-no-users">Nessun utente per questo filtro.</td></tr>
                )}
                {shown.map((u) => (
                  <tr key={u.id} className="border-t border-slate-100" data-testid={`relations-row-${u.id}`}>
                    <td className="p-2 align-top"><Checkbox checked={selected.has(u.id)} onCheckedChange={() => toggleSel(u.id)} data-testid={`relations-check-${u.id}`} /></td>
                    <td className="p-2 font-medium text-slate-700 align-top">{u.cognome} {u.nome}</td>
                    <td className="p-2">
                      <div className="flex flex-wrap items-center gap-1.5">
                        {(u.titular_workers || []).map((t) => (
                          <span key={t.id} className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 border border-emerald-200 px-2.5 py-1 text-xs text-emerald-800" data-testid={`relations-assigned-${u.id}-${t.id}`}>
                            <UserRound className="h-3 w-3" /> {t.name || "—"}
                            <button className="text-emerald-600 hover:text-red-500" title="Rimuovi" onClick={() => removeTitolare(u, t.id)} data-testid={`relations-remove-${u.id}-${t.id}`}><X className="h-3 w-3" /></button>
                          </span>
                        ))}
                        <div className="min-w-[170px] max-w-xs flex-1">
                          <EducatorSearch appaltoId={appaltoId} service={service} testid={`relations-search-${u.id}`}
                            placeholder={(u.titular_workers || []).length ? "Aggiungi operatore…" : "Cerca operatore…"}
                            onPick={(ed) => addTitolare(u, ed)} />
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

export function RelationsAdmin() {
  const [actionCount, setActionCount] = useState(0);
  const loadCount = () => api.get("/office/relations/action-count").then((r) => setActionCount(r.data?.count || 0)).catch(() => {});
  useEffect(() => { loadCount(); }, []);
  return (
    <Tabs defaultValue="educatori" className="w-full" data-testid="relations-tabs">
      <TabsList className="mb-4" data-testid="relations-tabs-list">
        <TabsTrigger value="educatori" data-testid="relations-tab-educatori">
          <UserCog className="h-4 w-4 mr-1.5" /> Operatori titolari
        </TabsTrigger>
        <TabsTrigger value="gestione" data-testid="relations-tab-gestione">
          <FileText className="h-4 w-4 mr-1.5" /> Gestione relazioni
          {actionCount > 0 && (
            <span data-testid="relations-gestione-badge" className="ml-1.5 inline-flex items-center justify-center min-w-[20px] h-5 px-1 rounded-full bg-amber-500 text-white text-[11px] font-bold">{actionCount}</span>
          )}
        </TabsTrigger>
        <TabsTrigger value="archivio" data-testid="relations-tab-archivio">
          <Archive className="h-4 w-4 mr-1.5" /> Archivio relazioni
        </TabsTrigger>
      </TabsList>
      <TabsContent value="educatori"><EducatoriTitolari /></TabsContent>
      <TabsContent value="gestione"><RelationsManager onActionChange={loadCount} /></TabsContent>
      <TabsContent value="archivio"><RelationsArchive /></TabsContent>
    </Tabs>
  );
}

export default RelationsAdmin;
